/**
 * External Imports
 */

import { connect, useDispatch } from 'react-redux';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react';
/**
 * Internal Imports
 */
import TransitionalBarChart from '../../metricsChart/barChart/TransitionalBarChart';
import { RootState } from '../../../redux/store';
import {
  setDelegatedEmployee,
  setFeedbackFilter
} from '../../../redux/actions';
import { logReportsClick, ReportEvents } from '../../../utils/analyticsHelpers';
/**
 * Global Type Definition Imports
 */
import {
  ApiMetrics,
  Filter,
  IDelegateOption,
  MetricsData
} from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  HeaderContainer,
  TaskMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';

interface EmpRecognitionMetricChartProps {
  metrics: ApiMetrics;
  employeeSummaryReportFilter: Filter;
  selectedDelegate: IDelegateOption;
}

const EmpRecognitionMetricChart: FC<EmpRecognitionMetricChartProps> = ({
  metrics,
  employeeSummaryReportFilter,
  selectedDelegate
}) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();

  const metricsData: MetricsData = useMemo(() => {
    return {
      totalRecordsCount: metrics.totalRecordsCount,
      metrics: [
        {
          label: 'Opened',
          value: metrics.openedRecordsCount,
          color: '#df5828'
        },
        {
          label: 'Completed',
          value: metrics.completedRecordsCount,
          color: '#0077b5'
        }
      ]
    };
  }, [metrics]);

  const openFeedbackTable = () => {
    dispatch(
      setFeedbackFilter({
        ...employeeSummaryReportFilter,
        startDate:
          employeeSummaryReportFilter.startDate ??
          format(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            'yyyy-MM-dd'
          ),
        category: '',
        subCategory: '',
        createdBy: '',
        status: '',
        taskSubType: 'recognition',
        employeeId: ''
      })
    );
    dispatch(setDelegatedEmployee(selectedDelegate));
    if (view)
      logTrackingEvent(
        logReportsClick(view?.replace('/', '-'), 'recognition' as ReportEvents)
      );
    setTimeout(() => {
      navigateTo('/employees/feedback', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <TaskMetricContainer onClick={openFeedbackTable}>
      <HeaderContainer style={{ paddingLeft: '3em' }}>
        <NeuIcon size="1x" color="blue" className="ms-1">
          flag
        </NeuIcon>
        <TitleContainer style={{ marginLeft: '0.5rem' }}>
          {metrics.totalRecordsCount} Recognition
        </TitleContainer>
      </HeaderContainer>
      <TransitionalBarChart metricsData={metricsData} />
    </TaskMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    employeeSummaryReportFilter:
      state.ReportReducer.employeeSummaryReportFilter,
    selectedDelegate: state.EmployeeReducer.selectedDelegatedEmployee,
    metrics: state.TaskReducer.recognitionMetrics
  };
};
export default connect(mapReduxStateToProps)(EmpRecognitionMetricChart);
